import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { Button } from '../../../shared/ui';
import { IPsyBlock } from '../new-questions/new-question-psy';
import { setActiveTool } from '../../../store/meetActiveTool.slice';
import { PsyToolsId } from '../../meet/tools-panel/types';
import { useMobileToolSheet } from '../../meet/tools-panel/mobile-tool-sheet';

import { EmoView } from './emo-view';

export interface IDetectorEmotionPsy extends IPsyBlock {
  sendEmotionsOpen?: () => void;
  emotionsClientInbound?: { nonce: number; result: { category: string[]; emo: string[] } } | null;
  onEmotionsClientInboundConsumed?: () => void;
  /** Мобильный 1:1: панель внутри нижней шторки поверх видео (заголовок рисует шторка) */
  mobile?: boolean;
}

export const DetectorEmotionPsy: FC<IDetectorEmotionPsy> = ({
  result,
  setResult,
  onToolActivated,
  sendEmotionsOpen,
  emotionsClientInbound,
  onEmotionsClientInboundConsumed,
  mobile = false,
}) => {
  const sheet = useMobileToolSheet();
  const psyTools = useAppSelector((state) => state.psyToolsSlice.tools);
  const dispatch = useAppDispatch();
  const [reactionId, setReactionId] = useState<string[]>([]);
  const [resultEmo, setResultEmo] = useState<string[]>([]);
  const [isClientChoosing, setClientChoosing] = useState(false);

  const settings = useMemo(
    () => psyTools.find((el) => el.name === 'emotions_detection')?.psy_tool_settings,
    [psyTools],
  );

  const createUpdateEmotions = useCallback(
    (cat: string[], emo: string[]) => {
      if (cat.length === 0) return;
      const data = settings?.filter((el) => cat.includes(el.name));
      const data1 = data?.map((el) => {
        return { ...el, value: el.value.filter((el) => emo.includes(el)) };
      });
      const data2 = data1
        ?.filter((el) => el.value.length > 0)
        .map((el) => {
          return { category: el.name, items: el.value };
        });
      const update = {
        date_time: new Date().toISOString(),
        comment: '',
        emotions: data2 || [],
      };
      if (result) {
        setResult({ ...result, emotions: [...result.emotions, update] });
      }
    },
    [result, setResult, settings],
  );

  useEffect(() => {
    if (!emotionsClientInbound) return;

    const { result: inboundResult } = emotionsClientInbound;
    createUpdateEmotions(inboundResult.category ?? [], inboundResult.emo ?? []);
    setResultEmo(inboundResult.emo ?? []);
    // Мобильный: клиент выбрал эмоции — раскрываем шторку с результатом
    sheet?.expand();
    onEmotionsClientInboundConsumed?.();
  }, [emotionsClientInbound, createUpdateEmotions, onEmotionsClientInboundConsumed, sheet]);

  const requestEmotions = () => {
    setClientChoosing(true);
    sendEmotionsOpen?.();
    onToolActivated?.('emotions_detection');
    dispatch(setActiveTool(PsyToolsId.EMOTIONS));
    // Мобильный: убираем шторку, чтобы видеть клиента, пока он выбирает
    sheet?.collapse();
  };

  const screen0 = (
    <div className="text-body-l text-white p-[1rem]">
      Вы уверены, что хотите запросить у клиента его эмоциональное состояние в данный момент?
    </div>
  );
  const screenLast = (
    <div className="scroll-white overflow-y-auto mt-[1rem] mb-2.5">
      <div className=" flex gap-[15px] flex-wrap md:flex-col md:items-center w-full">
        {resultEmo.map((el, idx) => (
          <div
            className="w-full max-w-[240px] 2xl:max-w-[360px] bg-ink/60 rounded-chip py-[2px] px-0 lg:p-[5px_10px] text-center text-body-l-strong text-white mx-auto"
            key={idx}
          >
            {el}
          </div>
        ))}
      </div>
    </div>
  );

  const screenClientChoosing = (
    <div className="h-full max-h-full flex flex-col scroll-white overflow-y-auto p-[1rem]">
      <div className=" text-white pb-[2rem]">Клиент выбирает из списка эмоций. Выбранные эмоции отобразятся здесь.</div>
      <EmoView isPsiholog settings={settings} reactionId={reactionId} setReactionId={setReactionId} />
    </div>
  );

  if (mobile) {
    return (
      <div className="flex w-full flex-col gap-3 text-white">
        {!isClientChoosing ? (
          <>
            <p className="text-ui text-white/90">
              Вы уверены, что хотите запросить у клиента его эмоциональное состояние в данный момент?
            </p>
            <div className="sticky bottom-[-1rem] z-[60] -mx-4 bg-ink-muted px-4 pb-4 pt-3 shadow-overlay">
              <Button size="lg" fullWidth onClick={requestEmotions}>
                Запросить
              </Button>
            </div>
          </>
        ) : resultEmo.length === 0 ? (
          <>
            <p className="text-ui font-medium">Клиент выбирает из списка эмоций. Выбранные эмоции отобразятся здесь.</p>
            <EmoView isPsiholog mobile settings={settings} reactionId={reactionId} setReactionId={setReactionId} />
          </>
        ) : (
          <>
            <p className="text-ui text-white/80">Эмоции, которые выбрал клиент</p>
            <div className="flex flex-wrap gap-2">
              {resultEmo.map((el) => (
                <span key={el} className="rounded-full bg-black/20 px-3 py-1.5 text-ui font-medium text-white">
                  {el}
                </span>
              ))}
            </div>
            <div className="sticky bottom-[-1rem] z-[60] -mx-4 flex gap-2 bg-ink-muted px-4 pb-4 pt-3 shadow-overlay">
              <Button size="md" variant="ghostInverse" fullWidth onClick={() => sheet?.restart()}>
                Новый запрос
              </Button>
              <Button size="md" fullWidth onClick={() => sheet?.finish()}>
                Завершить
              </Button>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="sm:w-[30vw] lg:w-[15vw] md:min-w-[300px] h-full flex flex-col">
      <div className="flex mr-[3rem] ml-[1rem] mt-[1rem] text-display-s text-white">Детектор эмоций</div>
      {!isClientChoosing ? screen0 : resultEmo.length === 0 ? screenClientChoosing : screenLast}
      {resultEmo.length === 0 && (
        <div className="mt-auto flex flex-row">
          {!isClientChoosing && (
            <Button size="md" fullWidth onClick={requestEmotions}>
              Запросить
            </Button>
          )}
          <div className="min-w-[48px] w-[48px]"></div>
        </div>
      )}
    </div>
  );
};
